import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import { endPoint } from "utils/config";
import { readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

const SIGNUP_DRIVER = gql`
  mutation driverSignUpByAdmin(
    $fullName: String!
    $email: String!
    $password: String!
    $phoneNumber: String!
  ) {
    driverSignUpByAdmin(
      input: { fullName: $fullName, email: $email, password: $password, phoneNumber: $phoneNumber }
    ) {
      _id
      fullName
    }
  }
`;

/**
 * SignupDriver - create a driver account on someone's behalf.
 *
 * This only creates the account. The documents that get a driver
 * approved are a separate submission, so the form says so and offers to
 * go straight there rather than leaving you on a success modal with no
 * idea what happens next.
 */
const SignupDriver = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [visible, setVisible] = useState(false);

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const [signUpDriver] = useMutation(SIGNUP_DRIVER);

  const validate = () => {
    const next = {};
    if (!fullName.trim()) next.fullName = "A name is required.";
    if (!email.trim()) next.email = "An email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) next.email = "That does not look like an email.";
    if (!phoneNumber.trim()) next.phoneNumber = "A phone number is required.";
    if (!password) next.password = "Set a password for the new account.";
    else if (password.length < 8) next.password = "Use at least 8 characters.";
    if (password !== confirmPassword) next.confirmPassword = "The two passwords do not match.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    setFormError("");
    if (!validate()) return;

    setSaving(true);
    signUpDriver({
      variables: {
        fullName: fullName.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        password,
      },
    })
      .then((res) => {
        setSaving(false);
        setCreated(res.data.driverSignUpByAdmin);
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  if (created) {
    return (
      <div className="page superPeoplePage">
        <main className="main">
          <PageHeader
            title="Driver account created"
            description={`${created.fullName} can now sign in to the driver app.`}
          />
          <Panel title="What happens next">
            <div className="superPersonForm">
              <div className="dsFormMessage success" role="status">
                The account exists, but the driver cannot accept trips until their documents have
                been submitted and approved.
              </div>
              <div className="superPersonFooter">
                <Button
                  variant="secondary"
                  onClick={() => (window.location.href = `${endPoint}/superAdmin/drivers`)}
                >
                  Back to drivers
                </Button>
                <Button
                  variant="primary"
                  onClick={() => (window.location.href = `${endPoint}/superAdmin/addDriver`)}
                >
                  Submit their documents
                </Button>
              </div>
            </div>
          </Panel>
        </main>
      </div>
    );
  }

  return (
    <div className="page superPeoplePage">
      <main className="main">
        <PageHeader
          title="New driver account"
          description="Create a driver account. Their documents are submitted separately, afterwards."
        />

        <form onSubmit={onSubmit}>
          <Panel title="Account">
            <div className="superPersonForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Full name" htmlFor="fullName" error={errors.fullName} required>
                <input
                  id="fullName"
                  className="dsInput"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Field>

              <div className="superPersonFormRow">
                <Field label="Email" htmlFor="email" error={errors.email} required>
                  <input
                    id="email"
                    className="dsInput"
                    type="email"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>

                <Field label="Phone number" htmlFor="phoneNumber" error={errors.phoneNumber} required>
                  <input
                    id="phoneNumber"
                    className="dsInput"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </Field>
              </div>

              <Field
                label="Password"
                htmlFor="password"
                error={errors.password}
                required
                hint="The driver signs in with this. Share it with them separately."
              >
                <span className="superPersonValueRow">
                  <input
                    id="password"
                    className="dsInput"
                    type={visible ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisible((current) => !current)}
                    aria-label={visible ? "Hide password" : "Show password"}
                  >
                    {visible ? (
                      <VisibilityIcon fontSize="small" />
                    ) : (
                      <VisibilityOffIcon fontSize="small" />
                    )}
                  </Button>
                </span>
              </Field>

              <Field
                label="Confirm password"
                htmlFor="confirmPassword"
                error={errors.confirmPassword}
                required
              >
                <input
                  id="confirmPassword"
                  className="dsInput"
                  type={visible ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Field>
            </div>
          </Panel>

          <div className="superPersonFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/drivers`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              Create account
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SignupDriver;
